import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import { useMutation, useApolloClient, useLazyQuery } from "react-apollo";
import { Field, Control, Button } from "rbx";

import Section from "../Section";

import { CREATE_ORDER_DETAIL_MUTATION } from "../../graphql/Orders/Orders.graphql";
import { SINGLE_PRODUCT_QUERY } from "../../graphql/Products/Products.graphql";
import { ourToast as toast } from "../../utils";

import { SINGLE_SYSTEM_CODE_QUERY } from "../../graphql/SystemCodes/SystemCodes.graphql";

import OrderDetailBillingForm from "./OrderDetailBillingForm";
import OrderDetailDispatcherForm from "./OrderDetailDispatcherForm";

const INITIAL_STATE = {
  Quantity: 1,
  ProductRecIdRef: "0",
  Description: "",
  Hazmat: "",
  UOMRecIdRef: "",
  InternalComm: "",
  ExternalComm: "",
  Rate: "",
  Note: "",
  Percentage: "",
  ProdCatgRecIdRef: "",
  OverrideFlag: false
};

const AddOrderDetailForm = ({ setModalOpen, OrderRecIdRef, isDispatcher }) => {
  const client = useApolloClient();

  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const [createOrderDetail, { loading }] = useMutation(
    CREATE_ORDER_DETAIL_MUTATION,
    {
      refetchQueries: ["ALL_ORDER_DETAILS_QUERY"]
    }
  );

  const [getSystemCode, { data: systemCodeData }] = useLazyQuery(
    SINGLE_SYSTEM_CODE_QUERY
  );

  useEffect(() => {
    const UOMRecIdRef = parseInt(inputs.UOMRecIdRef, 10);
    if (UOMRecIdRef) {
      getSystemCode({
        variables: {
          where: {
            RecId: UOMRecIdRef
          }
        }
      });
    }
  }, [getSystemCode, inputs.UOMRecIdRef]);

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      await createOrderDetail({
        variables: {
          data: {
            CompanyId: "TRN",
            ProductRecIdRef: parseInt(inputs.ProductRecIdRef),
            Description: inputs.Description,
            Quantity: parseFloat(inputs.Quantity),
            UOM: parseInt(inputs.UOM),
            Hazmat: inputs.Hazmat,
            UOMRecIdRef: parseInt(inputs.UOMRecIdRef),
            OrderRecIdRef: parseInt(OrderRecIdRef),
            InternalComm: inputs.InternalComm,
            ExternalComm: inputs.ExternalComm,
            Rate: parseFloat(inputs.Rate),
            Percentage: parseFloat(inputs.Percentage),
            Note: inputs.Note,
            OverrideFlag: inputs.OverrideFlag
          }
        },
        refetchQueries: ["OrderDetails"]
      });
      setModalOpen(false);
      toast.success("Order Detail created successfully.");
    } catch (err) {
      toast.error("Error creating Order Detail.");
    }
  };

  const fetchProductData = useCallback(
    async productRecIdRef => {
      try {
        const { data: ProductData } = await client.query({
          query: SINGLE_PRODUCT_QUERY,
          variables: {
            where: {
              RecId: parseInt(productRecIdRef, 10)
            }
          }
        });
        if (ProductData && ProductData.Product) {
          setInputs(prev => ({
            ...prev,
            Rate: ProductData.Product.Price
              ? ProductData.Product.Price
              : prev.Rate,
            Hazmat: ProductData.Product.HazmatCode,
            ProdCatgRecIdRef: ProductData.Product.ProductCatgRecIdRef,
            Percentage: ProductData.Product.Percentage,
            UOMRecIdRef: ProductData.Product.UOMRecIdRef,
            Description: ProductData.Product.Description
          }));
        }
      } catch (err) {
        toast.error("Error getting Product.");
      }
    },
    [client]
  );
  const handleChange = (name, value) => {
    setInputs({ ...inputs, [name]: value });
    if (name === "ProductRecIdRef") {
      fetchProductData(value);
    }
  };

  const UOMCodeDescription = systemCodeData?.SystemCode?.CodeDescription;

  useEffect(() => {
    if (UOMCodeDescription === "Flat Fee") {
      setInputs(prev => ({ ...prev, Quantity: 1 }));
    }
  }, [UOMCodeDescription]);

  return (
    <form id="add-order-detail" method="POST" onSubmit={handleSubmit}>
      <fieldset aria-busy={loading}>
        <Section title="Add Order Detail">
          {isDispatcher ? (
            <OrderDetailDispatcherForm
              inputs={inputs}
              onChange={handleChange}
            />
          ) : (
            <OrderDetailBillingForm inputs={inputs} onChange={handleChange} />
          )}
          <hr />
          <Field kind="group">
            <Control>
              <Button color="primary" type="submit">
                Submit
              </Button>
            </Control>
            <Control>
              <Button onClick={() => setModalOpen(false, "")}>Cancel</Button>
            </Control>
          </Field>
        </Section>
      </fieldset>
    </form>
  );
};

AddOrderDetailForm.propTypes = {
  OrderRecIdRef: PropTypes.number,
  setModalOpen: PropTypes.func,
  isDispatcher: PropTypes.bool
};

export default AddOrderDetailForm;
